// src/page/milkdelivery/MilkVehicleDriverManagement.tsx
import React from "react";
import { Button, Modal, Table, Form } from "react-bootstrap";
import MilkDeliveryApi from "../../api/MilkDeliveryApi";
import AuthorizedPage from "../../component/AuthorizedPage";

interface Driver {
  id?: number;
  name: string;
  surname: string;
  gender: string;
  dateOfBirth: string; // "YYYY-MM-DD"
  phone: string;
  tckn: string;
}

interface State {
  drivers: Driver[];
  showModal: boolean;
  editingDriver: Driver | null;
  loading: boolean;
  saving: boolean;
}

const defaultDriver: Driver = {
  name: "",
  surname: "",
  gender: "",
  dateOfBirth: "",
  phone: "",
  tckn: "",
};

export default class MilkVehicleDriverManagement extends React.Component<{}, State> {
  constructor(props: {}) {
    super(props);
    this.state = {
      drivers: [],
      showModal: false,
      editingDriver: null,
      loading: false,
      saving: false,
    };
  }

  componentDidMount() {
    this.loadDrivers();
  }

  async loadDrivers() {
    this.setState({ loading: true });
    try {
      const res = await MilkDeliveryApi.listDrivers();
      // res biçimi: { success, data, message }
      if (res && res.success && Array.isArray(res.data)) {
        // Tarih alanında saat varsa sadece YYYY-MM-DD kısmını al
        const data: Driver[] = res.data.map((d: any) => ({
          id: d.id,
          name: d.name ?? "",
          surname: d.surname ?? "",
          gender: d.gender ?? "",
          dateOfBirth: d.dateOfBirth ? String(d.dateOfBirth).split("T")[0] : "",
          phone: d.phone ?? "",
          tckn: d.tckn ?? "",
        }));
        this.setState({ drivers: data });
      } else {
        console.error("Sürücüler yüklenemedi:", res);
        this.setState({ drivers: [] });
      }
    } catch (err) {
      console.error("Sürücüler yüklenirken hata:", err);
      this.setState({ drivers: [] });
    } finally {
      this.setState({ loading: false });
    }
  }

  openEditModal = (driver?: Driver) => {
    this.setState({
      editingDriver: driver ? { ...driver } : { ...defaultDriver },
      showModal: true,
    });
  };

  handleChange = (e: React.ChangeEvent<any>) => {
    if (!this.state.editingDriver) return;
    const { name, value } = e.target;
    this.setState(prev => ({
      editingDriver: {
        ...prev.editingDriver!,
        [name]: value,
      },
    }));
  };

  handleSave = async () => {
    const { editingDriver } = this.state;
    if (!editingDriver) return;

    // Basit validasyon (örnek)
    if (!editingDriver.name.trim() || !editingDriver.surname.trim()) {
      alert("Ad ve soyad zorunludur.");
      return;
    }

    this.setState({ saving: true });
    try {
      // dateOfBirth alanını sadece YYYY-MM-DD formatında gönder
      const payload = {
        ...editingDriver,
        dateOfBirth: editingDriver.dateOfBirth ? editingDriver.dateOfBirth.split("T")[0] : "",
      };

      const res = await MilkDeliveryApi.createOrUpdateDriver(payload);
      if (res && res.success) {
        await this.loadDrivers();
        this.setState({ showModal: false, editingDriver: null });
      } else {
        console.error("Kaydetme başarısız:", res);
      }
    } catch (err) {
      console.error("Kaydetme hatası:", err);
      alert("Kaydetme sırasında bir hata oluştu.");
    } finally {
      this.setState({ saving: false });
    }
  };

  renderTable() {
    return (
      <Table striped bordered hover size="sm">
        <thead>
          <tr>
            <th>Ad</th>
            <th>Soyad</th>
            <th>Cinsiyet</th>
            <th>Doğum Tarihi</th>
            <th>Telefon</th>
            <th>TCKN</th>
            <th>İşlem</th>
          </tr>
        </thead>
        <tbody>
          {this.state.drivers.map(d => (
            <tr key={d.id ?? Math.random()}>
              <td>{d.name}</td>
              <td>{d.surname}</td>
              <td>{d.gender}</td>
              <td>{d.dateOfBirth}</td>
              <td>{d.phone}</td>
              <td>{d.tckn}</td>
              <td>
                <Button size="sm" variant="outline-primary" onClick={() => this.openEditModal(d)}>
                  Düzenle
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    );
  }

  renderModal() {
    const { editingDriver, showModal, saving } = this.state;
    if (!editingDriver) return null;

    return (
      <Modal show={showModal} onHide={() => this.setState({ showModal: false, editingDriver: null })}>
        <Modal.Header closeButton>
          <Modal.Title>Sürücü Bilgileri</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Ad</Form.Label>
              <Form.Control name="name" value={editingDriver.name} onChange={this.handleChange} />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Soyad</Form.Label>
              <Form.Control name="surname" value={editingDriver.surname} onChange={this.handleChange} />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Cinsiyet</Form.Label>
              <Form.Select name="gender" value={editingDriver.gender} onChange={this.handleChange}>
                <option value="">Seçiniz</option>
                <option value="Erkek">Erkek</option>
                <option value="Kadın">Kadın</option>
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Doğum Tarihi</Form.Label>
              <Form.Control type="date" name="dateOfBirth" value={editingDriver.dateOfBirth} onChange={this.handleChange} />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Telefon</Form.Label>
              <Form.Control name="phone" value={editingDriver.phone} onChange={this.handleChange} />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>TCKN</Form.Label>
              <Form.Control name="tckn" value={editingDriver.tckn} onChange={this.handleChange} />
            </Form.Group>
          </Form>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={() => this.setState({ showModal: false, editingDriver: null })}>
            İptal
          </Button>
          <Button variant="primary" onClick={this.handleSave} disabled={saving}>
            {saving ? "Kaydediliyor..." : "Kaydet"}
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }

  render() {
    return (
      <AuthorizedPage role="ORDER_MANAGER" isLoading={this.state.loading}>
        <div className="container mt-4">
          <h4>Süt Araçları Sürücü Yönetimi</h4>
          <Button className="mb-3" onClick={() => this.openEditModal()}>
            + Sürücü Ekle
          </Button>

          {this.renderTable()}
          {this.renderModal()}
        </div>
      </AuthorizedPage>
    );
  }
}
